import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { RotateCcw, AlertTriangle, Calendar, FileText } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';
import type { JournalEntry } from '../../types/accounting';

interface ReverseJournalEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: JournalEntry | null;
  onConfirm: (data: { reversalDate?: string; description?: string }) => Promise<void>;
  loading?: boolean;
}

export function ReverseJournalEntryModal({
  isOpen,
  onClose,
  entry,
  onConfirm,
  loading = false,
}: ReverseJournalEntryModalProps) {
  const [reversalDate, setReversalDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const { formatConverted } = useCurrency();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onConfirm({
      reversalDate: reversalDate || undefined,
      description: description || undefined,
    });
  };

  const handleClose = () => {
    if (!loading) {
      setReversalDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      onClose();
    }
  };

  if (!entry) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Extourne d'écriture" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Avertissement */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-700 dark:text-amber-400">
            <p className="font-medium mb-1">Extourne SYSCOHADA</p>
            <p>
              Une écriture miroir sera créée en <span className="font-semibold">brouillon</span> avec 
              les montants débit/crédit inversés. L'écriture originale sera liée à l'extourne par 
              des références bidirectionnelles.
            </p>
          </div>
        </div>

        {/* Résumé de l'écriture originale */}
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Écriture originale
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Référence :</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{entry.reference}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Date :</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                {new Date(entry.date).toLocaleDateString('fr-FR')}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Total débit :</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                {formatConverted(entry.totalDebit)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Total crédit :</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                {formatConverted(entry.totalCredit)}
              </span>
            </div>
          </div>
          {/* Lignes preview */}
          {entry.lines && entry.lines.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{entry.lines.length} ligne(s) — les montants seront inversés</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {entry.lines.slice(0, 5).map((line, idx) => (
                  <div key={idx} className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span className="truncate flex-1">{line.accountCode} — {line.accountName}</span>
                    <span className="ml-2 whitespace-nowrap">
                      {line.debit > 0 ? `D ${formatConverted(line.debit)}` : `C ${formatConverted(line.credit)}`}
                      {' → '}
                      {line.debit > 0 ? `C ${formatConverted(line.debit)}` : `D ${formatConverted(line.credit)}`}
                    </span>
                  </div>
                ))}
                {entry.lines.length > 5 && (
                  <p className="text-xs text-gray-400 italic">... et {entry.lines.length - 5} autre(s)</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Formulaire */}
        <div className="space-y-4">
          <div>
            <label htmlFor="reversalDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              Date de l'extourne
            </label>
            <input
              type="date"
              id="reversalDate"
              value={reversalDate}
              onChange={(e) => setReversalDate(e.target.value)}
              className="form-input w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:ring-primary focus:border-primary"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Par défaut, la date d'aujourd'hui est utilisée.
            </p>
          </div>

          <div>
            <label htmlFor="reversalDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Libellé de l'extourne <span className="text-gray-400 font-normal">(optionnel)</span>
            </label>
            <textarea
              id="reversalDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`Extourne ${entry.reference}`}
              rows={2}
              className="form-textarea w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="warning"
            icon={RotateCcw}
            isLoading={loading}
          >
            Créer l'extourne
          </Button>
        </div>
      </form>
    </Modal>
  );
}
